import { App as AntApp, Button, Card, Col, Empty, Row, Skeleton, Space, Statistic, Table, Tag, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useAgentLeaderboard,
  useCashflow,
  useDashboard,
  useOverdueAging,
  useProjectProfitability,
  useSalesFunnel,
  type ProfitabilityRow,
} from '../api/reports';
import { downloadFromApi } from '@/shared/lib/download';
import { asApiError } from '@/shared/lib/api';
import { formatUzs, formatUzsCompact } from '@/shared/lib/format';

export function DashboardPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data: dash, isLoading } = useDashboard();
  const { data: funnel } = useSalesFunnel();
  const { data: cashflow } = useCashflow(12);
  const { data: aging } = useOverdueAging();
  const { data: leaderboard } = useAgentLeaderboard();
  const { data: profitability } = useProjectProfitability();

  async function download(path: string, file: string): Promise<void> {
    try {
      await downloadFromApi(path, file);
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>{t('reports.dashboard')}</Typography.Title>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => void download('/reports/payments.csv', 'payments-1c.csv')}
          >
            {t('reports.exportPaymentsCsv')}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => void download('/reports/cashflow.csv', 'cashflow.csv')}
          >
            {t('reports.exportCashflowCsv')}
          </Button>
        </Space>
      </div>

      {isLoading ? (
        <Skeleton active />
      ) : !dash ? (
        <Empty />
      ) : (
        <>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Card><Statistic title={t('reports.activeContracts')} value={dash.contracts.active} /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title={t('reports.completedContracts')} value={dash.contracts.completed} valueStyle={{ color: '#16a34a' }} /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title={t('reports.paymentsToday')}
                  value={`${formatUzsCompact(dash.paymentsToday.total || '0')}`}
                  suffix={`(${dash.paymentsToday.count})`}
                  valueStyle={{ color: '#16a34a' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title={t('reports.overdue')}
                  value={`${formatUzsCompact(dash.overdue.outstanding || '0')}`}
                  suffix={`(${dash.overdue.rows})`}
                  valueStyle={{ color: '#dc2626' }}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card title={t('reports.apartments')}>
                <Row gutter={8}>
                  {Object.entries(dash.apartments).map(([s, n]) => (
                    <Col key={s} xs={12} md={8}>
                      <Tag style={{ padding: '4px 10px', fontSize: 13 }}>{t(`apartments.status.${s}`)}</Tag>{' '}
                      <Typography.Text strong>{n}</Typography.Text>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title={t('reports.salesFunnel')}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {(funnel?.stages ?? []).map((s) => (
                    <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography.Text type="secondary">{t(`owner.funnel.${s.key}`)}</Typography.Text>
                      <Typography.Text strong>{s.count}</Typography.Text>
                    </div>
                  ))}
                </Space>
              </Card>
            </Col>
          </Row>

          <Card title={t('reports.cashflowNext12Months')}>
            <Table
              rowKey="month"
              dataSource={cashflow ?? []}
              pagination={false}
              size="small"
              columns={[
                { title: t('reports.month'), dataIndex: 'month' },
                { title: t('reports.expectedDue'), dataIndex: 'expectedDue', render: (v: string) => formatUzs(v) },
                { title: t('reports.projected'), dataIndex: 'projected', render: (v: string) => formatUzs(v) },
              ]}
            />
          </Card>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card title={t('reports.overdueAging')}>
                <Table
                  rowKey="range"
                  dataSource={aging ?? []}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: t('reports.range'), dataIndex: 'range' },
                    { title: t('reports.schedules'), dataIndex: 'schedules' },
                    { title: t('reports.principal'), dataIndex: 'principal', render: (v: string) => formatUzs(v) },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title={t('reports.agentLeaderboard')}>
                <Table
                  rowKey="userId"
                  dataSource={leaderboard ?? []}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: t('reports.agent'), dataIndex: 'fullName' },
                    { title: t('reports.customers'), dataIndex: 'customers' },
                    { title: t('reports.contracts'), dataIndex: 'contracts' },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Card
            title={t('reports.projectProfitability')}
            extra={
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  void download('/reports/project-profitability.csv', 'project-profitability.csv')
                }
              >
                CSV
              </Button>
            }
          >
            <Table<ProfitabilityRow>
              rowKey="projectId"
              dataSource={profitability ?? []}
              pagination={false}
              size="small"
              columns={[
                { title: t('reports.code'), dataIndex: 'code', width: 100 },
                { title: t('reports.project'), dataIndex: 'name' },
                { title: t('reports.contractsCount'), dataIndex: 'contracts', width: 120 },
                {
                  title: t('reports.contractedTotal'),
                  dataIndex: 'contractedTotal',
                  render: (v: string) => formatUzs(v),
                },
                {
                  title: t('reports.collectedTotal'),
                  dataIndex: 'collectedTotal',
                  render: (v: string) => formatUzs(v),
                },
                {
                  title: t('reports.collectionRate'),
                  dataIndex: 'collectionRatePercent',
                  width: 120,
                  render: (v: number) => `${v.toFixed(1)}%`,
                },
                {
                  title: t('reports.overdueOutstanding'),
                  dataIndex: 'overdueOutstanding',
                  render: (v: string) => formatUzs(v),
                },
              ]}
            />
          </Card>
        </>
      )}
    </Space>
  );
}
